<template>
    <GuestLayout :absolute-header="false" :dark="false">
        <!-- Hero Section with Gradient Background -->
        <div class="bg-gradient-to-br from-cyan-600 via-cyan-700 to-blue-800">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <!-- Breadcrumbs -->
                <nav class="flex mb-8" aria-label="Breadcrumb">
                    <ol class="flex items-center space-x-4">
                        <li>
                            <Link
                                :href="route('listings.index')"
                                class="text-cyan-100 hover:text-white"
                            >
                                <HomeIcon class="h-5 w-5" />
                                <span class="sr-only">Home</span>
                            </Link>
                        </li>
                        <li class="flex items-center">
                            <ChevronRightIcon class="h-5 w-5 text-cyan-200" />
                            <Link
                                :href="route('listings.category', categorySlug)"
                                class="ml-4 text-sm font-medium text-cyan-100 hover:text-white"
                                >{{ category }}</Link
                            >
                        </li>
                        <li class="flex items-center">
                            <ChevronRightIcon class="h-5 w-5 text-cyan-200" />
                            <span
                                class="ml-4 text-sm font-medium text-cyan-100"
                                >{{ title }}</span
                            >
                        </li>
                    </ol>
                </nav>

                <!-- Job Header -->
                <div
                    class="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8"
                >
                    <div class="flex items-center gap-6">
                        <!-- Company Logo -->
                        <div class="bg-white p-4 rounded-xl shadow-lg">
                            <img
                                v-if="companyLogo"
                                :src="companyLogo"
                                :alt="companyName"
                                class="h-16 w-16 object-contain"
                            />
                            <div
                                v-else
                                class="h-16 w-16 rounded-lg bg-cyan-50 flex items-center justify-center"
                            >
                                <span
                                    class="text-cyan-600 text-xl font-semibold"
                                >
                                    {{ companyName.charAt(0) }}
                                </span>
                            </div>
                        </div>

                        <!-- Job Title & Company -->
                        <div>
                            <div class="flex items-center gap-3 mb-1">
                                <h1 class="text-3xl font-bold text-white">
                                    {{ title }}
                                </h1>
                                <span
                                    v-if="isFeatured"
                                    class="bg-yellow-400 text-yellow-900 text-xs font-medium px-2.5 py-0.5 rounded-full"
                                >
                                    Featured
                                </span>
                            </div>
                            <p class="text-xl text-cyan-100">
                                {{ companyName }}
                            </p>
                            <p v-if="position" class="text-cyan-200 mt-1">
                                {{ position }}
                            </p>
                        </div>
                    </div>

                    <!-- Posted Date -->
                    <div
                        class="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 text-cyan-50"
                    >
                        Posted {{ postedDateFromNow }}
                    </div>
                </div>

                <!-- Job Highlights Grid -->
                <div
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
                >
                    <!-- Employment Type -->
                    <div
                        v-if="commitment"
                        class="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3"
                    >
                        <BriefcaseIcon class="h-6 w-6 text-cyan-200" />
                        <div>
                            <p class="text-cyan-200 text-sm">Employment Type</p>
                            <p class="text-white">{{ commitment }}</p>
                        </div>
                    </div>

                    <!-- Location -->
                    <div
                        v-if="location || remoteLabel"
                        class="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3"
                    >
                        <MapPinIcon class="h-6 w-6 text-cyan-200" />
                        <div>
                            <p class="text-cyan-200 text-sm">Location</p>
                            <p class="text-white">
                                {{ location || remoteLabel }}
                            </p>
                        </div>
                    </div>

                    <!-- Experience -->
                    <div
                        v-if="experience"
                        class="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3"
                    >
                        <ClockIcon class="h-6 w-6 text-cyan-200" />
                        <div>
                            <p class="text-cyan-200 text-sm">Experience</p>
                            <p class="text-white">{{ experience }}</p>
                        </div>
                    </div>

                    <!-- Salary Range -->
                    <div
                        v-if="salaryRange"
                        class="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3"
                    >
                        <CurrencyDollarIcon class="h-6 w-6 text-cyan-200" />
                        <div>
                            <p class="text-cyan-200 text-sm">Salary Range</p>
                            <p class="text-white">{{ salaryRange }}</p>
                        </div>
                    </div>
                </div>

                <!-- Benefits -->
                <div v-if="benefits && benefits.length" class="mb-8">
                    <h3 class="text-cyan-100 font-medium mb-3">Benefits</h3>
                    <div class="flex flex-wrap gap-2">
                        <span
                            v-for="benefit in benefits"
                            :key="benefit"
                            class="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-sm text-white"
                        >
                            {{ benefit }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="bg-gray-50">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <!-- Warning Banner (same as before) -->

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <!-- Requirements -->
                        <div
                            v-if="requirements"
                            class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
                        >
                            <h2
                                class="text-xl font-semibold text-gray-900 mb-4"
                            >
                                Requirements
                            </h2>
                            <div
                                class="prose prose-cyan max-w-none"
                                v-html="requirements"
                            ></div>
                        </div>

                        <!-- Required Skills -->
                        <div
                            v-if="tags && tags.length"
                            class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8"
                        >
                            <h2
                                class="text-xl font-semibold text-gray-900 mb-4"
                            >
                                Required Skills
                            </h2>
                            <div class="flex flex-wrap gap-2">
                                <Link
                                    v-for="tag in tags"
                                    :key="tag.id"
                                    :href="
                                        route('listings.tag', {
                                            slug: tag.slug,
                                        })
                                    "
                                >
                                    <span
                                        class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-cyan-50 text-cyan-800"
                                    >
                                        {{ tag.name }}
                                    </span>
                                </Link>
                            </div>
                        </div>

                        <!-- Job Description -->
                        <div
                            class="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                        >
                            <h2
                                class="text-xl font-semibold text-gray-900 mb-4"
                            >
                                Job Description
                            </h2>
                            <div
                                class="prose prose-cyan max-w-none"
                                v-html="description"
                            ></div>
                        </div>
                    </div>

                    <!-- Sidebar -->
                    <div class="lg:col-span-1">
                        <div class="sticky top-4">
                            <div
                                class="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                            >
                                <h2
                                    class="text-xl font-semibold text-gray-900 mb-4"
                                >
                                    How to Apply
                                </h2>
                                <div v-if="isActive">
                                    <ApplyButton
                                        v-if="isActive"
                                        :apply-method="applyMethod"
                                        :apply-link="applyLink"
                                        :apply-email="applyEmail"
                                        class="w-full bg-cyan-600 hover:bg-cyan-700"
                                    />
                                    <div
                                        v-if="applyMethod === 'email'"
                                        class="mt-3 text-gray-600"
                                    >
                                        <p class="text-center mb-2">
                                            Send your CV and cover letter to:
                                        </p>
                                        <p class="text-center">
                                            <span class="font-medium">{{
                                                applyEmail.replace(
                                                    "mailto:",
                                                    "",
                                                )
                                            }}</span>
                                        </p>
                                        <p
                                            class="text-center mt-2 text-gray-500 text-sm"
                                        >
                                            Please include the job title in your
                                            email subject
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    v-else
                                    disabled
                                    class="w-full cursor-not-allowed"
                                >
                                    Job Posting Expired
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Related Listings -->
                <RelatedListings
                    v-if="relatedListings.data.length > 0"
                    :listings="relatedListings"
                    class="mt-12"
                />
            </div>
        </div>
    </GuestLayout>
</template>

<script setup>
import GuestLayout from "@/Layouts/GuestLayout.vue";
import {
    BriefcaseIcon,
    MapPinIcon,
    ClockIcon,
    CurrencyDollarIcon,
} from "@heroicons/vue/24/outline";
import { HomeIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import { Link } from "@inertiajs/vue3";
import Button from "@/Components/Guest/Button.vue";
import ApplyButton from "@/Components/Guest/ApplyButton.vue";
import RelatedListings from "@/Components/Guest/Listings/RelatedListings.vue";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { inject } from "vue";
const route = inject("route");

const props = defineProps({
    listing: Object,
    relatedListings: Object,
});

const {
    title,
    companyName,
    companyLogo,
    postedDate,
    salaryRange,
    experience,
    commitment,
    category,
    categorySlug,
    remoteStatus,
    remoteLabel,
    tags,
    description,
    applyMethod,
    applyLink,
    applyEmail,
    isActive,
    isFeatured,
    benefits,
    requirements,
    position,
    location,
} = props.listing;

dayjs.extend(relativeTime);
let postedDateFromNow = dayjs(postedDate).fromNow();
</script>
